import { useState, useCallback, useRef, useMemo } from 'react';
import {
  Upload, FileVideo, Image, X, CheckCircle, AlertCircle,
  ChevronDown, ChevronRight, Subtitles, Sparkles, Link2,
} from 'lucide-react';
import { uploadFile } from '../../services/upload';
import type {
  UploadProgress,
  CaptionSettings,
  UploadStitchGroupMetadata,
} from '../../types';
import {
  DEFAULT_CAPTION_SETTINGS,
  CAPTION_POSITION_OPTIONS,
  CAPTION_TEXT_CASE_OPTIONS,
  CAPTION_EMOJI_DENSITY_OPTIONS,
} from '../../types';
import { useCaptionTemplates } from '../../hooks/useApi';

interface FileEntry {
  file: File;
  title: string;
  description: string;
  progress: UploadProgress | null;
  captionSettings?: CaptionSettings;
}

interface UploadZoneProps {
  onUploadComplete?: (contentId: string) => void;
  onAllUploadsComplete?: (contentIds: string[]) => void;
}

const ACCEPTED_TYPES = [
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'video/x-msvideo',
  'video/x-matroska',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function makeUploadGroupId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `upload-group-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function UploadZone({ onUploadComplete, onAllUploadsComplete }: UploadZoneProps) {
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [sharedCaptionSettings, setSharedCaptionSettings] = useState<CaptionSettings>({ ...DEFAULT_CAPTION_SETTINGS });
  const [stitchGroupedVideos, setStitchGroupedVideos] = useState(false);
  const [uploadGroupId, setUploadGroupId] = useState<string>(() => makeUploadGroupId());
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const entries: FileEntry[] = Array.from(newFiles)
      .filter((f) => ACCEPTED_TYPES.includes(f.type))
      .map((file) => ({
        file,
        title: file.name.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' '),
        description: '',
        progress: null,
        ...(file.type.startsWith('video/')
          ? { captionSettings: { ...DEFAULT_CAPTION_SETTINGS } }
          : {}),
      }));

    setFiles((prev) => [...prev, ...entries]);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files.length > 0) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles],
  );

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateFile = useCallback((index: number, updates: Partial<FileEntry>) => {
    setFiles((prev) =>
      prev.map((f, i) => (i === index ? { ...f, ...updates } : f)),
    );
  }, []);

  const updateCaptionSettings = useCallback((index: number, settings: CaptionSettings) => {
    setFiles((prev) =>
      prev.map((f, i) => (i === index ? { ...f, captionSettings: settings } : f)),
    );
  }, []);

  const videoEntries = useMemo(
    () => files.filter((entry) => entry.file.type.startsWith('video/')),
    [files],
  );

  const canUseStitchMode = videoEntries.length > 1;

  const buildSharedStitchMetadata = useCallback((videoIndex: number, totalVideos: number): UploadStitchGroupMetadata => ({
    enabled: true,
    groupId: uploadGroupId,
    sourceIndex: videoIndex,
    sourceCount: totalVideos,
    sharedCaptionSettings,
  }), [sharedCaptionSettings, uploadGroupId]);

  const handleUploadAll = useCallback(async () => {
    setIsUploading(true);
    const completedIds: string[] = [];

    const stitchedVideoIndexes = files.reduce<number[]>((acc, entry, index) => {
      if (stitchGroupedVideos && entry.file.type.startsWith('video/')) {
        acc.push(index);
      }
      return acc;
    }, []);

    for (let i = 0; i < files.length; i++) {
      const entry = files[i]!;
      if (entry.progress?.status === 'complete') continue;

      const stitchedVideoIndex = stitchedVideoIndexes.indexOf(i);
      const stitchGroup = stitchedVideoIndex >= 0
        ? buildSharedStitchMetadata(stitchedVideoIndex, stitchedVideoIndexes.length)
        : undefined;
      const effectiveCaptionSettings = stitchGroup?.sharedCaptionSettings ?? entry.captionSettings;

      try {
        const contentId = await uploadFile(
          entry.file,
          entry.title,
          entry.description,
          (progress) => updateFile(i, { progress }),
          effectiveCaptionSettings,
          stitchGroup,
        );
        completedIds.push(contentId);
        onUploadComplete?.(contentId);
      } catch {
        // Error already captured in progress state
      }
    }

    setIsUploading(false);

    if (completedIds.length > 0) {
      onAllUploadsComplete?.(completedIds);
      setUploadGroupId(makeUploadGroupId());
    }
  }, [
    buildSharedStitchMetadata,
    files,
    onAllUploadsComplete,
    onUploadComplete,
    stitchGroupedVideos,
    updateFile,
  ]);

  const pendingFiles = files.filter(
    (f) => !f.progress || f.progress.status === 'error',
  );
  const canUpload = pendingFiles.length > 0 && !isUploading;

  return (
    <div className="space-y-6">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-12 text-center transition-all ${
          isDragging
            ? 'border-brand-green bg-brand-green-50 scale-[1.01]'
            : 'border-gray-300 hover:border-brand-green hover:bg-gray-50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPTED_TYPES.join(',')}
          onChange={(e) => e.target.files && addFiles(e.target.files)}
          className="hidden"
        />
        <Upload
          className={`mx-auto h-12 w-12 ${isDragging ? 'text-brand-green' : 'text-gray-400'}`}
        />
        <h3 className="mt-4 text-lg font-semibold text-gray-900">
          Drop files here or click to browse
        </h3>
        <p className="mt-2 text-sm text-gray-500">
          Supports MP4, MOV, WebM, AVI, MKV, JPEG, PNG, WebP, GIF — up to 5GB
        </p>
      </div>

      {files.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">
              {files.length} file{files.length !== 1 ? 's' : ''} selected
            </h3>
            {canUpload && (
              <button onClick={handleUploadAll} className="btn-primary">
                <Upload className="mr-2 h-4 w-4" />
                Upload All
              </button>
            )}
          </div>

          {canUseStitchMode && (
            <div className="rounded-xl border border-brand-green/20 bg-brand-green/5 p-4">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={stitchGroupedVideos}
                  onChange={(e) => setStitchGroupedVideos(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-brand-green focus:ring-brand-green"
                  disabled={isUploading}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <Link2 className="h-4 w-4 text-brand-green" />
                    Stitch these {videoEntries.length} videos into one grouped result
                  </div>
                  <p className="mt-1 text-sm text-gray-600">
                    When enabled, every video in this batch keeps the same shared Captions.ai configuration so the final stitched deliverable lands with one consistent caption treatment.
                  </p>
                </div>
              </label>

              {stitchGroupedVideos && (
                <div className="mt-4">
                  <CaptionSettingsPanel
                    settings={sharedCaptionSettings}
                    onChange={setSharedCaptionSettings}
                    title="Shared stitched-result caption settings"
                    helperText="This one caption config is saved on the upload group and reused for the stitched result."
                  />
                </div>
              )}
            </div>
          )}

          {files.map((entry, index) => (
            <FileCard
              key={`${entry.file.name}-${index}`}
              entry={entry}
              index={index}
              onUpdate={updateFile}
              onRemove={removeFile}
              onUpdateCaptionSettings={updateCaptionSettings}
              isUploading={isUploading}
              usingSharedCaptionSettings={stitchGroupedVideos && entry.file.type.startsWith('video/')}
              sharedCaptionSettings={sharedCaptionSettings}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface FileCardProps {
  entry: FileEntry;
  index: number;
  onUpdate: (index: number, updates: Partial<FileEntry>) => void;
  onRemove: (index: number) => void;
  onUpdateCaptionSettings: (index: number, settings: CaptionSettings) => void;
  isUploading: boolean;
  usingSharedCaptionSettings: boolean;
  sharedCaptionSettings: CaptionSettings;
}

function FileCard({
  entry,
  index,
  onUpdate,
  onRemove,
  onUpdateCaptionSettings,
  isUploading,
  usingSharedCaptionSettings,
  sharedCaptionSettings,
}: FileCardProps) {
  const { file, title, description, progress, captionSettings } = entry;
  const isVideo = file.type.startsWith('video/');
  const isComplete = progress?.status === 'complete';
  const isError = progress?.status === 'error';
  const isActive = progress != null && !isComplete && !isError;

  return (
    <div
      className={`card p-4 transition-all ${
        isComplete ? 'border-green-200 bg-green-50/50' : isError ? 'border-red-200 bg-red-50/50' : ''
      }`}
    >
      <div className="flex items-start gap-4">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
            isVideo ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'
          }`}
        >
          {isVideo ? <FileVideo className="h-5 w-5" /> : <Image className="h-5 w-5" />}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-medium text-gray-900">{file.name}</p>
            <span className="shrink-0 text-xs text-gray-500">{formatFileSize(file.size)}</span>
            {isComplete && <CheckCircle className="h-4 w-4 shrink-0 text-green-500" />}
            {isError && <AlertCircle className="h-4 w-4 shrink-0 text-red-500" />}
          </div>

          {!isComplete && (
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <input
                type="text"
                value={title}
                onChange={(e) => onUpdate(index, { title: e.target.value })}
                placeholder="Title"
                className="input"
                disabled={isActive}
              />
              <input
                type="text"
                value={description}
                onChange={(e) => onUpdate(index, { description: e.target.value })}
                placeholder="Description (optional)"
                className="input"
                disabled={isActive}
              />
            </div>
          )}

          {isVideo && captionSettings && !isComplete && !isActive && !usingSharedCaptionSettings && (
            <CaptionSettingsPanel
              settings={captionSettings}
              onChange={(s) => onUpdateCaptionSettings(index, s)}
            />
          )}

          {isVideo && !isComplete && !isActive && usingSharedCaptionSettings && (
            <SharedCaptionSettingsNotice templateId={sharedCaptionSettings.templateId} />
          )}

          {progress && isActive && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>
                  {progress.status === 'uploading'
                    ? 'Uploading...'
                    : progress.status === 'registering'
                      ? 'Registering...'
                      : 'Pending...'}
                </span>
                <span>{progress.progress}%</span>
              </div>
              <div className="mt-1 h-2 overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-full rounded-full bg-brand-green transition-all duration-300"
                  style={{ width: `${progress.progress}%` }}
                />
              </div>
            </div>
          )}

          {isError && progress?.error && (
            <p className="mt-2 text-xs text-red-600">{progress.error}</p>
          )}
        </div>

        {!isActive && !isComplete && (
          <button
            onClick={() => onRemove(index)}
            className="shrink-0 rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            disabled={isUploading}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

interface CaptionSettingsPanelProps {
  settings: CaptionSettings;
  onChange: (settings: CaptionSettings) => void;
  title?: string;
  helperText?: string;
}

function CaptionSettingsPanel({
  settings,
  onChange,
  title = 'Caption Settings',
  helperText,
}: CaptionSettingsPanelProps) {
  const [isOpen, setIsOpen] = useState(false);

  const update = <K extends keyof CaptionSettings>(key: K, value: CaptionSettings[K]) => {
    onChange({ ...settings, [key]: value });
  };

  return (
    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50/50">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
      >
        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <Subtitles className="h-4 w-4 text-gray-500" />
        <span>{title}</span>
        <span className="ml-auto inline-flex items-center gap-1 text-xs font-normal text-purple-600">
          <Sparkles className="h-3.5 w-3.5" />
          Captions.ai only
        </span>
      </button>

      {isOpen && (
        <div className="space-y-4 border-t border-gray-200 px-3 py-3">
          <div className="rounded-lg border border-purple-100 bg-purple-50 px-3 py-2 text-xs text-purple-800">
            Videos now use Captions.ai for caption generation and burn-in. Template selection is applied today, and the rest of this config is stored alongside the upload so grouped stitched edits keep a consistent caption intent.
          </div>

          {helperText && (
            <p className="text-xs text-gray-600">{helperText}</p>
          )}

          <CaptionTemplateSelect
            value={settings.templateId}
            onChange={(id) => update('templateId', id)}
          />

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                Vertical Position
              </label>
              <select
                value={settings.position ?? DEFAULT_CAPTION_SETTINGS.position}
                onChange={(e) => update('position', e.target.value as CaptionSettings['position'])}
                className="input w-full"
              >
                {CAPTION_POSITION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                Text Case
              </label>
              <select
                value={settings.textCase ?? DEFAULT_CAPTION_SETTINGS.textCase}
                onChange={(e) => update('textCase', e.target.value as CaptionSettings['textCase'])}
                className="input w-full"
              >
                {CAPTION_TEXT_CASE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                Emoji Density
              </label>
              <select
                value={settings.emojiDensity ?? DEFAULT_CAPTION_SETTINGS.emojiDensity}
                onChange={(e) => update('emojiDensity', e.target.value as CaptionSettings['emojiDensity'])}
                className="input w-full"
              >
                {CAPTION_EMOJI_DENSITY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <NumberInput
              label="Max Words Per Caption"
              value={settings.maxWordsPerCaption ?? DEFAULT_CAPTION_SETTINGS.maxWordsPerCaption}
              min={1}
              max={12}
              onChange={(value) => update('maxWordsPerCaption', value)}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <ToggleField
              label="Highlight current word"
              checked={settings.highlightCurrentWord ?? DEFAULT_CAPTION_SETTINGS.highlightCurrentWord}
              onChange={(value) => update('highlightCurrentWord', value)}
            />
            <ToggleField
              label="Censor profanity"
              checked={settings.profanityFilter ?? DEFAULT_CAPTION_SETTINGS.profanityFilter}
              onChange={(value) => update('profanityFilter', value)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

interface ToggleFieldProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function ToggleField({ label, checked, onChange }: ToggleFieldProps) {
  return (
    <label className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
      <span>{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-gray-300 text-brand-green focus:ring-brand-green"
      />
    </label>
  );
}

interface NumberInputProps {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}

function NumberInput({ label, value, min, max, onChange }: NumberInputProps) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
        {label}
      </label>
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="input w-full"
      />
    </div>
  );
}

interface CaptionTemplateSelectProps {
  value: string | undefined;
  onChange: (id: string) => void;
}

function CaptionTemplateSelect({ value, onChange }: CaptionTemplateSelectProps) {
  const { data: templates, loading, error } = useCaptionTemplates();
  const resolvedValue = value ?? templates?.[0]?.id ?? '';

  return (
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
        Caption Template
      </label>
      <select
        value={resolvedValue}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading || !templates?.length}
        className="input w-full"
      >
        {loading && <option value="">Loading templates…</option>}
        {!loading && (!templates || templates.length === 0) && (
          <option value="">No templates available</option>
        )}
        {templates?.map((t) => (
          <option key={t.id} value={t.id}>{t.name}</option>
        ))}
      </select>
      {error && (
        <p className="mt-1 text-xs text-red-600">
          Couldn't load templates from Captions.ai: {error}
        </p>
      )}
    </div>
  );
}

function SharedCaptionSettingsNotice({ templateId }: { templateId: string }) {
  const { data: templates } = useCaptionTemplates();
  const name = templates?.find((t) => t.id === templateId)?.name ?? 'Custom';
  return (
    <div className="mt-3 rounded-lg border border-purple-100 bg-purple-50 px-3 py-2 text-xs text-purple-800">
      This video will use the batch’s shared stitched-result caption settings: template <strong>{name}</strong>.
    </div>
  );
}
